import { useEffect, useState } from "react"
import { faClose, faImage, faPeopleGroup } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { CopyDraftAlert } from "../../../components/CopyDraftAlert/CopyDraftAlert"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { InputPhotoWithContainer } from "../../../components/InputPhoto/InputPhoto"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import { useFileImage } from "../../../hooks/useFileImage"
import type { RestaurantReservationArea } from "../../../Objects/RestaurantReservation/RestaurantReservationTypes"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import GetSpace from "../../../utils/space"

interface ReservationAreaPopupProps {
  area?: RestaurantReservationArea
  isLoading: boolean
  onClose: () => void
  onCopy?: () => void
  onRemove?: (area: RestaurantReservationArea) => Promise<boolean>
  onSave: (area: RestaurantReservationArea, icon: File | null) => Promise<boolean>
}

const emptyArea = (): RestaurantReservationArea => ({
  id: 0,
  name: "",
  iconUrl: "",
  capacity: 20,
  isActive: true,
  displayOrder: 0,
})

export const ReservationAreaPopup: React.FC<ReservationAreaPopupProps> = ({
  area,
  isLoading,
  onClose,
  onCopy,
  onRemove,
  onSave,
}) => {
  const { t } = useTranslation("restaurantReservations")

  const isExistingArea = !!area && area.id > 0

  const isCopiedDraft = area?.id === 0

  const [draft, setDraft] = useState<RestaurantReservationArea>(() => (area ? { ...area } : emptyArea()))

  const { fileImage, setFileImage } = useFileImage()

  useEffect(() => {
    setDraft(area ? { ...area } : emptyArea())
  }, [area])

  const hasSelectedPhoto = !!fileImage && fileImage.name !== "__remove_icon__"

  const hasPhoto = hasSelectedPhoto || (!!draft.iconUrl && fileImage?.name !== "__remove_icon__")

  const statusOptions = [
    { label: t("common.active"), value: "true" },
    { label: t("common.inactive"), value: "false" },
  ]

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={isExistingArea ? t("management.edit_area") : t("management.new_area")}
              icon={faImage}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <GenericDropDown
                    data={statusOptions}
                    initialState={String(draft.isActive)}
                    handlerFuncion={(event) =>
                      setDraft((current) => ({ ...current, isActive: event.target.value === "true" }))
                    }
                    descriptionKey="interactionDescriptions:restaurantReservations.areaPopup.active"
                  />

                  <GetSpace right={5} />

                  <PopupHelpButton
                    helpKey="restaurant-reservation-area"
                    variant="global"
                    title={t("help.area.title")}
                    description={t("help.area.description")}
                    bullets={t("help.area.bullets", { returnObjects: true }) as string[]}
                  />

                  <GetSpace right={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:restaurantReservations.areaPopup.close"
                  />
                </div>
              }
            />

            {isCopiedDraft && <CopyDraftAlert />}

            <form
              className="p-2"
              onSubmit={async (event) => {
                event.preventDefault()
                if (await onSave(draft, fileImage)) onClose()
              }}
            >
              <InputPhotoWithContainer
                initialPhoto={draft.iconUrl}
                handleFileSelected={setFileImage}
                imageProfile="default"
              />

              <div className={defaultStyle.divisionBar} />

              <InputField
                type="text"
                icon={faImage}
                value={draft.name}
                maxLength={80}
                required
                placeholder={t("fields.area_name")}
                onInvalidMessage={t("validation.area_name")}
                onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                descriptionKey="interactionDescriptions:restaurantReservations.areaPopup.name"
              />

              <GetSpace top={10} />

              <InputField
                type="number"
                icon={faPeopleGroup}
                value={String(draft.capacity)}
                min={1}
                max={1000}
                required
                placeholder={t("fields.capacity")}
                onInvalidMessage={t("validation.capacity")}
                onChange={(event) => setDraft((current) => ({ ...current, capacity: Number(event.target.value) }))}
                descriptionKey="interactionDescriptions:restaurantReservations.areaPopup.capacity"
              />

              <div className={defaultStyle.divisionBar} />

              <div className={defaultStyle.buttonsController}>
                <Button
                  label={isLoading ? t("common.saving") : t("actions.save_area")}
                  typeButton="submit"
                  type="primary"
                  size="medium"
                  disabled={isLoading || !draft.name.trim() || draft.capacity < 1 || !hasPhoto}
                  bgColor={THEME.colors.buttonBGSaveColor}
                  descriptionKey="interactionDescriptions:restaurantReservations.areaPopup.save"
                />

                {isExistingArea && onCopy && (
                  <>
                    <GetSpace right={5} />

                    <Button
                      label={t("buttons:button_copy")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGPlusColor}
                      onClick={onCopy}
                      descriptionKey="interactionDescriptions:restaurantReservations.areaPopup.copy"
                    />
                  </>
                )}

                {isExistingArea && onRemove && (
                  <>
                    <GetSpace right={5} />

                    <Button
                      label={t("actions.remove_area")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGRemoveColor}
                      onClick={async () => {
                        if (await onRemove(draft)) onClose()
                      }}
                      descriptionKey="interactionDescriptions:restaurantReservations.areaPopup.remove"
                    />
                  </>
                )}
              </div>
            </form>
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}
